import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Typography, Space } from 'antd';
import { BugOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import { Button } from '@bancobarigui/barihaus';

const { Text, Paragraph } = Typography;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ errorInfo });

    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/';
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div
          style={{
            minHeight: '100vh',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#f0f2f5',
            padding: '24px',
          }}
        >
          <Result
            icon={<BugOutlined style={{ color: '#ff4d4f' }} />}
            title="Algo deu errado"
            subTitle={
              <Space direction="vertical" size="small">
                <Text type="secondary">
                  Ocorreu um erro inesperado ao carregar esta pagina.
                </Text>
                <Text type="secondary">
                  Por favor, tente recarregar a pagina ou volte para o inicio.
                </Text>
                {this.state.error && process.env.NODE_ENV === 'development' && (
                  <Paragraph
                    type="danger"
                    code
                    style={{
                      maxWidth: '600px',
                      marginTop: '16px',
                      fontSize: '12px',
                      textAlign: 'left',
                    }}
                  >
                    {this.state.error.message}
                  </Paragraph>
                )}
              </Space>
            }
            extra={
              <Space>
                <Button buttonColor="primary" icon={<ReloadOutlined />} onClick={this.handleReload}>
                  Recarregar pagina
                </Button>
                <Button icon={<HomeOutlined />} onClick={this.handleGoHome}>
                  Voltar ao inicio
                </Button>
              </Space>
            }
          />
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
